import type { CadenceDaySummary, CadencePeriod, CadenceStatus, CadenceTask } from "../api/cadence.ts";
import type { Lead } from "../types.ts";

export type CalendarCell = {
  date: string;
  dayNumber: number;
  inMonth: boolean;
  isToday: boolean;
  summary?: CadenceDaySummary;
};

export type CadenceTaskFilters = {
  funnelId: string;
  stage: string;
  priority: string;
  campaign: string;
};

export type CadenceMonthMetrics = {
  pending: number;
  done: number;
  skipped: number;
  snoozed: number;
  late: number;
  total: number;
};

export type QuickCadencePreset = "tomorrow" | "in3" | "in7" | "nextMonday";

const WEEK_LENGTH = 7;
const GRID_DAYS = 42;

export function isoMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function todayIsoDate(now = new Date()): string {
  return isoDate(now);
}

export function addMonths(month: string, amount: number): string {
  const [year, monthIndex] = parseMonth(month);
  return isoMonth(new Date(year, monthIndex - 1 + amount, 1));
}

export function cadenceMonthLabel(month: string): string {
  const [year, monthIndex] = parseMonth(month);
  return new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" })
    .format(new Date(year, monthIndex - 1, 1))
    .toLowerCase();
}

export function buildCalendarGrid(
  month: string,
  days: CadenceDaySummary[] = [],
  today = todayIsoDate(),
): CalendarCell[] {
  const [year, monthIndex] = parseMonth(month);
  const first = new Date(year, monthIndex - 1, 1);
  const mondayOffset = (first.getDay() + 6) % WEEK_LENGTH;
  const gridStart = new Date(year, monthIndex - 1, 1 - mondayOffset);
  const summaries = new Map(days.map((day) => [day.date, day]));

  return Array.from({ length: GRID_DAYS }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    const dateText = isoDate(date);
    return {
      date: dateText,
      dayNumber: date.getDate(),
      inMonth: date.getMonth() === monthIndex - 1,
      isToday: dateText === today,
      summary: summaries.get(dateText),
    };
  });
}

export function countPendingTasks(days: CadenceDaySummary[]): number {
  return days.reduce((total, day) => total + Number(day.pending || 0), 0);
}

export function cadenceMonthMetrics(days: CadenceDaySummary[]): CadenceMonthMetrics {
  return days.reduce((metrics, day) => ({
    pending: metrics.pending + Number(day.pending || 0),
    done: metrics.done + Number(day.done || 0),
    skipped: metrics.skipped + Number(day.skipped || 0),
    snoozed: metrics.snoozed + Number(day.snoozed || 0),
    late: metrics.late + Number(day.late || 0),
    total: metrics.total + Number(day.total || 0),
  }), { pending: 0, done: 0, skipped: 0, snoozed: 0, late: 0, total: 0 });
}

export function tasksForDate(days: CadenceDaySummary[], date: string): CadenceTask[] {
  return days.find((day) => day.date === date)?.tasks || [];
}

export function filterCadenceTasks(
  tasks: CadenceTask[],
  leads: Lead[],
  filters: CadenceTaskFilters,
): CadenceTask[] {
  const leadsById = new Map(leads.map((lead) => [String(lead.id), lead]));
  const campaignQuery = normalizeSearch(filters.campaign);
  return tasks.filter((task) => {
    const lead = leadsById.get(String(task.leadId));
    const funnelId = String(task.leadFunnelId || lead?.funnelId || "");
    const stage = String(task.leadStage || lead?.stage || "");
    const priority = String(task.leadPriority || lead?.priority || "");
    const campaignText = normalizeSearch(`${lead?.campaign || ""} ${lead?.course || ""}`);

    if (filters.funnelId && funnelId !== filters.funnelId) return false;
    if (filters.stage && stage !== filters.stage) return false;
    if (filters.priority && priority !== filters.priority) return false;
    if (campaignQuery && !campaignText.includes(campaignQuery)) return false;
    return true;
  });
}

export function filterCadenceLeads(leads: Lead[], tasks: CadenceTask[], query: string): Lead[] {
  const normalizedQuery = normalizeSearch(query);
  const scheduledLeadIds = new Set(tasks.map((task) => String(task.leadId)));
  return leads
    .filter((lead) => lead.id !== undefined && lead.id !== null)
    .filter((lead) => {
      if (!normalizedQuery) return true;
      return normalizeSearch(`${lead.name || ""} ${lead.phone || ""} ${lead.email || ""} ${lead.course || ""}`).includes(normalizedQuery);
    })
    .sort((a, b) => {
      const aScheduled = scheduledLeadIds.has(String(a.id)) ? 1 : 0;
      const bScheduled = scheduledLeadIds.has(String(b.id)) ? 1 : 0;
      if (aScheduled !== bScheduled) return bScheduled - aScheduled;
      return String(a.name || a.phone || "").localeCompare(String(b.name || b.phone || ""), "pt-BR");
    })
    .slice(0, 80);
}

export function periodLabel(period: CadencePeriod): string {
  if (period === "manha") return "Manha";
  if (period === "tarde") return "Tarde";
  if (period === "noite") return "Noite";
  return "Qualquer horario";
}

export function statusLabel(status: CadenceStatus): string {
  if (status === "done") return "Concluido";
  if (status === "skipped") return "Pulou";
  if (status === "snoozed") return "Remarcado";
  return "Pendente";
}

export function calendarCellClassName(cell: CalendarCell, selectedDate: string): string {
  return [
    "cadence-day",
    cell.inMonth ? "" : "is-muted",
    cell.isToday ? "is-today" : "",
    cell.date === selectedDate ? "is-selected" : "",
    Number(cell.summary?.late || 0) > 0 ? "has-late" : "",
  ].filter(Boolean).join(" ");
}

export function selectedLeadSummary(count: number): string {
  if (count === 0) return "Nenhum lead selecionado";
  if (count === 1) return "1 lead selecionado";
  return `${count} leads selecionados`;
}

export function quickCadenceDate(preset: QuickCadencePreset, baseDate = new Date()): string {
  const date = new Date(baseDate);
  if (preset === "tomorrow") date.setDate(date.getDate() + 1);
  if (preset === "in3") date.setDate(date.getDate() + 3);
  if (preset === "in7") date.setDate(date.getDate() + 7);
  if (preset === "nextMonday") {
    const daysUntilMonday = ((8 - date.getDay()) % WEEK_LENGTH) || WEEK_LENGTH;
    date.setDate(date.getDate() + daysUntilMonday);
  }
  return isoDate(date);
}

function parseMonth(month: string): [number, number] {
  const [yearText, monthText] = month.split("-");
  const year = Number(yearText);
  const monthIndex = Number(monthText);
  if (!year || !monthIndex || monthIndex < 1 || monthIndex > 12) {
    const now = new Date();
    return [now.getFullYear(), now.getMonth() + 1];
  }
  return [year, monthIndex];
}

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function normalizeSearch(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}
